import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function RolesPermissions() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ name:'', label:'', description:'' });
  const [editPerms, setEditPerms] = useState([]);

  const load = () => {
    setLoading(true);
    Promise.all([get('/roles'), get('/roles/permissions')]).then(([r, p]) => {
      setRoles(r.data||[]);
      setAllPermissions(p.data||[]);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const selectRole = (role) => {
    setSelected(role);
    setEditPerms(role.permissions||[]);
  };

  const togglePerm = (key) => {
    setEditPerms(prev =>
      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
    );
  };

  const savePermissions = async () => {
    setSaving(true);
    try {
      await put('/roles/'+selected.id, {
        label: selected.label,
        description: selected.description,
        permissions: editPerms
      });
      load();
      alert('Permissions saved for '+selected.label);
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const createRole = async () => {
    if (!newForm.name || !newForm.label) { alert('Name and label required'); return; }
    setSaving(true);
    try {
      await post('/roles', newForm);
      setShowNewForm(false);
      setNewForm({ name:'', label:'', description:'' });
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const deleteRole = async (role) => {
    if (role.is_system) { alert('System roles cannot be deleted'); return; }
    if (!window.confirm('Delete role "'+role.label+'"?')) return;
    try { await del('/roles/'+role.id); load(); setSelected(null); }
    catch(err) { alert(err.message); }
  };

  // Group permissions
  const groups = [...new Set(allPermissions.map(p => p.group))];

  if (user?.role !== 'super_admin') return (
    <div className="empty-state"><h3>Access Restricted</h3></div>
  );

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">🔐 Roles & Permissions</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Create roles and configure what each role can access
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNewForm(true)}>
          + New Role
        </button>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'280px 1fr', gap:20}}>
        {/* Role list */}
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
          <div style={{padding:'10px 16px',background:'#F2EFE7',fontSize:10,fontWeight:600,
            color:'#556B2F',textTransform:'uppercase',letterSpacing:'.08em'}}>
            Roles ({roles.length})
          </div>
          {loading ? <div className="spinner" style={{margin:20}}></div> : roles.map(r => (
            <div key={r.id} onClick={() => selectRole(r)}
              style={{padding:'12px 16px',cursor:'pointer',borderBottom:'1px solid #F2EFE7',
                background:selected?.id===r.id?'#F2EFE7':'#fff'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div style={{fontSize:13,fontWeight:500,color:'#3B2A21'}}>{r.label}</div>
                {r.is_system && (
                  <span style={{fontSize:8,background:'#EDF4FF',color:'#1E4FAD',
                    padding:'1px 6px',borderRadius:10,fontWeight:600}}>System</span>
                )}
              </div>
              <div style={{fontSize:10,color:'#8A8A8A',fontFamily:'monospace',marginTop:2}}>{r.name}</div>
              <div style={{fontSize:10,color:'#556B2F',marginTop:2}}>
                {r.permissions?.includes('*') ? 'All permissions' : (r.permissions?.length||0)+' permissions'}
              </div>
            </div>
          ))}
        </div>

        {/* Permission editor */}
        <div>
          {!selected ? (
            <div className="empty-state">
              <h3>Select a role</h3>
              <p>Click a role to view and edit its permissions</p>
            </div>
          ) : (
            <div>
              <div style={{background:'#fff',border:'1px solid var(--border-light)',
                borderRadius:10,padding:20,marginBottom:16}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div>
                    <h3 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.2rem',
                      color:'#3B2A21',margin:0}}>{selected.label}</h3>
                    <div style={{fontSize:11,color:'#8A8A8A',fontFamily:'monospace',marginTop:2}}>{selected.name}</div>
                    {selected.description && (
                      <div style={{fontSize:12,color:'#555',marginTop:4}}>{selected.description}</div>
                    )}
                  </div>
                  {!selected.is_system && (
                    <button className="btn btn-ghost btn-sm" style={{color:'#8F2E24'}}
                      onClick={() => deleteRole(selected)}>Delete Role</button>
                  )}
                </div>
              </div>

              {selected.permissions?.includes('*') ? (
                <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:10,padding:20}}>
                  <div style={{fontSize:14,fontWeight:600,color:'#2E6B3A'}}>
                    ✅ Full Access — All permissions granted
                  </div>
                  <div style={{fontSize:12,color:'#555',marginTop:4}}>
                    This is the Managing Director role with unrestricted access.
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                    <div style={{fontSize:12,color:'#8A8A8A'}}>
                      {editPerms.length} of {allPermissions.length} permissions enabled
                    </div>
                    <div style={{display:'flex',gap:8}}>
                      <button className="btn btn-ghost btn-sm" style={{fontSize:11}}
                        onClick={()=>setEditPerms(allPermissions.map(p=>p.key))}>Select All</button>
                      <button className="btn btn-ghost btn-sm" style={{fontSize:11}}
                        onClick={()=>setEditPerms([])}>Clear All</button>
                      <button className="btn btn-primary btn-sm" onClick={savePermissions} disabled={saving}>
                        {saving?'Saving...':'Save Permissions'}
                      </button>
                    </div>
                  </div>

                  {groups.map(group => (
                    <div key={group} style={{background:'#fff',border:'1px solid var(--border-light)',
                      borderRadius:10,overflow:'hidden',marginBottom:12}}>
                      <div style={{padding:'10px 16px',background:'#3B2A21',
                        display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <span style={{fontSize:12,fontWeight:600,color:'#A8893D'}}>{group}</span>
                        <div style={{display:'flex',gap:8}}>
                          <button style={{fontSize:10,color:'rgba(242,239,231,.6)',
                            background:'none',border:'none',cursor:'pointer'}}
                            onClick={()=>{
                              const groupPerms = allPermissions.filter(p=>p.group===group).map(p=>p.key);
                              setEditPerms(prev => [...new Set([...prev,...groupPerms])]);
                            }}>All</button>
                          <button style={{fontSize:10,color:'rgba(242,239,231,.6)',
                            background:'none',border:'none',cursor:'pointer'}}
                            onClick={()=>{
                              const groupPerms = allPermissions.filter(p=>p.group===group).map(p=>p.key);
                              setEditPerms(prev => prev.filter(p=>!groupPerms.includes(p)));
                            }}>None</button>
                        </div>
                      </div>
                      <div style={{padding:'8px 16px',display:'grid',
                        gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:4}}>
                        {allPermissions.filter(p=>p.group===group).map(perm => (
                          <label key={perm.key} style={{display:'flex',alignItems:'center',
                            gap:8,padding:'6px 4px',cursor:'pointer',borderRadius:4,
                            background:editPerms.includes(perm.key)?'rgba(85,107,47,.05)':'transparent'}}>
                            <input type="checkbox"
                              checked={editPerms.includes(perm.key)}
                              onChange={()=>togglePerm(perm.key)}
                              style={{accentColor:'#556B2F',width:14,height:14}} />
                            <span style={{fontSize:12,color:editPerms.includes(perm.key)?'#3B2A21':'#8A8A8A'}}>
                              {perm.label}
                            </span>
                            <span style={{fontSize:9,color:'#AAAAAA',fontFamily:'monospace'}}>
                              {perm.key}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div style={{textAlign:'right',marginTop:12}}>
                    <button className="btn btn-primary" onClick={savePermissions} disabled={saving}>
                      {saving?'Saving...':'Save Permissions'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* New Role Modal */}
      {showNewForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:440}}>
            <div className="modal-hdr">
              <span className="modal-title">Create New Role</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowNewForm(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Role Name (system key) *</label>
                <input className="form-control" value={newForm.name}
                  onChange={e=>setNewForm({...newForm,name:e.target.value.toLowerCase().replace(/\s/g,'_')})}
                  placeholder="e.g. front_desk_officer" />
                <div className="form-hint">Lowercase, underscores only. This cannot be changed later.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Display Label *</label>
                <input className="form-control" value={newForm.label}
                  onChange={e=>setNewForm({...newForm,label:e.target.value})}
                  placeholder="e.g. Expenses & Compliance Officer" />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={newForm.description}
                  onChange={e=>setNewForm({...newForm,description:e.target.value})}
                  placeholder="What does this role do?" />
              </div>
              <div style={{fontSize:11,color:'#8A8A8A'}}>
                After creating the role, select it to configure its permissions.
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowNewForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={createRole} disabled={saving}>
                {saving?'Creating...':'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
